import classNames from "classnames";
import * as React from "react";

import styles from "./FormField.module.css";

export interface FormFieldProps {
  label: string;
  /** Id of the control the label names; omit when the control is not a single input. */
  htmlFor?: string;
  /** Id put on the label so a composite control can point at it. */
  labelId?: string;
  help?: React.ReactNode;
  /** Show the help under the control (for text you typed) rather than above it (for a choice). */
  helpBelow?: boolean;
  /** A validation message shown under the control. */
  error?: string;
  /** Rendered at the right end of the label row (a count, a small action). */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * One labelled control inside a form card: label, help, the control, and
 * anything wrong with it. The error sits under its own control rather than
 * beside the submit button, so a draft with two problems shows both.
 */
export function FormField({
  label,
  htmlFor,
  labelId,
  help,
  helpBelow,
  error,
  trailing,
  children,
  className,
}: FormFieldProps) {
  const labelNode =
    htmlFor !== undefined ? (
      <label className={styles.label} htmlFor={htmlFor} id={labelId}>
        {label}
      </label>
    ) : (
      <span className={styles.label} id={labelId}>
        {label}
      </span>
    );
  return (
    <div className={classNames(styles.field, className)}>
      <div className={styles.labelRow}>
        {labelNode}
        {trailing}
      </div>
      {help !== undefined && !helpBelow && (
        <p className={styles.help}>{help}</p>
      )}
      {children}
      {help !== undefined && helpBelow && <p className={styles.help}>{help}</p>}
      {error !== undefined && <p className={styles.error}>{error}</p>}
    </div>
  );
}

/** Two (or more) fields side by side, collapsing to one column when narrow. */
export function FormRow({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={classNames(styles.row, className)}>{children}</div>;
}

/** A rule between groups of fields inside one card. */
export function FormDivider() {
  return <hr className={styles.divider} />;
}

/**
 * A titled group inside a card for repeated content (conditions, variables):
 * label and help on the left, a count or control on the right, then the
 * content.
 */
export function FormSubsection({
  label,
  help,
  trailing,
  children,
  className,
}: {
  label: string;
  help?: React.ReactNode;
  trailing?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={classNames(styles.field, className)}>
      <div className={styles.subsectionHeader}>
        <div>
          <span className={styles.label}>{label}</span>
          {help !== undefined && <p className={styles.help}>{help}</p>}
        </div>
        {trailing}
      </div>
      {children}
    </div>
  );
}

/** A mono count beside a subsection label ("2 conditions"). */
export function FormCount({ children }: { children: React.ReactNode }) {
  return <span className={styles.count}>{children}</span>;
}

/** A text-styled action beside a control ("View sample event"). */
export function FormLinkButton({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={classNames(styles.link, className)}
      {...props}
    />
  );
}
